import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet } from "react-router";
import { useConnect, useConnection, useConnectors, useDisconnect, useSwitchChain } from "wagmi";
import { DEMO, chain, publicApiOrigin } from "../config";
import { shortAddr } from "../lib/format";
import { SignalField } from "./SignalField";

const NAV = [{ to: "/", label: "Explore", end: true }, { to: "/spawn", label: "Spawn" }, { to: "/b", label: "Boards" }, { to: "/prompt", label: "Genesis" }, { to: "/docs", label: "Docs" }];
export function ConnectButton() {
  const { address, chainId, status } = useConnection();
  const connectors = useConnectors();
  const { connectAsync, isPending: isConnecting } = useConnect();
  const { disconnectAsync, isPending: isDisconnecting } = useDisconnect();
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingConnectorId, setPendingConnectorId] = useState<string | null>(null);
  const operationPending = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("click", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (open) closeButtonRef.current?.focus();
    else if (wasOpen.current) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  const clearError = () => setError(null);
  const togglePicker = () => {
    clearError();
    setOpen((wasOpen) => !wasOpen);
  };

  const chooseWallet = async (connector: (typeof connectors)[number]) => {
    if (operationPending.current || isConnecting || isSwitching || isDisconnecting) return;
    operationPending.current = true;
    setError(null);
    setPendingConnectorId(connector.id);
    setOpen(false);
    try {
      await connectAsync({ connector, chainId: chain.id });
    } catch (cause) {
      setError(walletErrorMessage(cause));
      setOpen(true);
    } finally {
      operationPending.current = false;
      setPendingConnectorId(null);
    }
  };

  const switchToBase = async () => {
    if (operationPending.current || isSwitching || isConnecting || isDisconnecting) return;
    operationPending.current = true;
    setError(null);
    try {
      await switchChainAsync({ chainId: chain.id });
      setOpen(false);
    } catch {
      setError("Network switch failed or was cancelled. Try again in your wallet.");
    } finally {
      operationPending.current = false;
    }
  };

  const disconnectWallet = async () => {
    if (operationPending.current || isSwitching || isConnecting || isDisconnecting) return;
    operationPending.current = true;
    setError(null);
    try {
      await disconnectAsync();
      setOpen(false);
    } catch {
      setError("Could not disconnect the wallet. Try again.");
    } finally {
      operationPending.current = false;
    }
  };

  if (status === "connected" && address) {
    return <div className="menu" ref={ref}>
      <button ref={triggerRef} className="btn ghost" aria-expanded={open} aria-haspopup="true" onClick={togglePicker}>{shortAddr(address)}</button>
      {open && <div className="menu-pop panel hud on" aria-label="Wallet account menu">
        <NavLink to="/me" onClick={() => setOpen(false)}>Purchases created by you</NavLink>
        {chainId !== chain.id && <button className="linkish" disabled={isSwitching} onClick={() => void switchToBase()}>{isSwitching ? "Switching to Base…" : "Switch to Base"}</button>}
        <button className="linkish" disabled={isDisconnecting} onClick={() => void disconnectWallet()}>{isDisconnecting ? "Disconnecting…" : "Disconnect"}</button>
        {error && <p className="wallet-error" role="alert">{error}</p>}
      </div>}
    </div>;
  }

  const hasWalletConnect = connectors.some((connector) => connector.id === "walletConnect");
  const busy = isConnecting || isSwitching || isDisconnecting || pendingConnectorId !== null;
  return <div className="menu" ref={ref}>
    <button ref={triggerRef} className="btn" aria-expanded={open} aria-haspopup="dialog" disabled={busy} onClick={togglePicker}>
      {busy && pendingConnectorId ? `Connecting to ${connectors.find((connector) => connector.id === pendingConnectorId)?.name ?? "wallet"}…` : "Connect wallet"}
    </button>
    {open && typeof document !== "undefined" && createPortal(<div className="wallet-picker-backdrop" onClick={() => setOpen(false)}>
      <section className="wallet-picker panel" role="dialog" aria-modal="true" aria-labelledby="wallet-picker-title" onClick={(event) => event.stopPropagation()} onKeyDown={trapDialogFocus}>
        <header className="wallet-picker-header">
          <h2 id="wallet-picker-title">Choose a wallet</h2>
          <button ref={closeButtonRef} type="button" className="wallet-picker-close" aria-label="Close wallet picker" onClick={() => setOpen(false)}>×</button>
        </header>
        <p className="muted">Connecting only shares your wallet address after you approve in your wallet. It does not sign a message or submit a transaction.</p>
        {connectors.length > 0 ? <div className="wallet-options" aria-label="Available wallets">
          {connectors.map((connector) => <button
            key={`${connector.uid}:${connector.id}`}
            type="button"
            className="wallet-option"
            disabled={busy}
            aria-busy={pendingConnectorId === connector.id || undefined}
            onClick={() => void chooseWallet(connector)}
          >
            <span>{connector.name}</span>
            {pendingConnectorId === connector.id && <span aria-hidden="true">Connecting…</span>}
          </button>)}
        </div> : <p className="wallet-empty" role="status">No compatible wallet was detected in this browser.</p>}
        {!hasWalletConnect && <p className="wallet-configuration" role="status">Mobile wallet connection is currently unavailable. Open this site in your wallet browser or try again later.</p>}
        {error && <p className="wallet-error" role="alert">{error}</p>}
      </section>
    </div>, document.body)}
  </div>;
}

function walletErrorMessage(cause: unknown): string {
  const error = cause && typeof cause === "object" ? cause as { code?: unknown; name?: unknown; message?: unknown } : {};
  const code = error.code;
  const name = typeof error.name === "string" ? error.name.toLowerCase() : "";
  const message = typeof error.message === "string" ? error.message.toLowerCase() : "";
  if (code === 4001 || name.includes("rejected") || message.includes("user rejected") || message.includes("request rejected") || message.includes("modal closed")) {
    return "Connection cancelled. Choose a wallet to try again.";
  }
  if (message.includes("project id") || message.includes("projectid")) return "WalletConnect is not configured for this site yet.";
  if (name.includes("notfound") || message.includes("provider not found") || message.includes("wallet not found")) {
    return "The selected wallet was not detected. Open its browser or extension and retry.";
  }
  if (code === -32002 || message.includes("already pending")) return "Your wallet already has a pending request. Finish or cancel it in your wallet, then retry.";
  return "Wallet connection failed. Try again.";
}

function trapDialogFocus(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key !== "Tab") return;
  const dialog = event.currentTarget;
  const focusable = Array.from(dialog.querySelectorAll<HTMLElement>("button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"));
  if (focusable.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
function StatusStrip(){return <div className="status-strip"><div className="container status-inner"><span>REGISTRY V2</span><span className="faint">·</span><span>{publicApiOrigin?"PUBLIC DATA SOURCE CONFIGURED":"PUBLIC DATA UNAVAILABLE"}</span><span className="status-right faint">On-chain records do not verify hosting health</span></div></div>}
function DemoStrip(){return <div className="status-strip"><div className="container status-inner"><span>PREVIEW</span><span className="faint">·</span><span>PUBLIC V2 DATA UNAVAILABLE</span><span className="status-right faint">WALLET ACTIONS DISABLED</span></div></div>}
export function Layout(){useEffect(()=>window.scrollTo(0,0),[]);return <><SignalField/><div className="app"><header className="header"><div className="container header-inner"><NavLink to="/" className="brand" aria-label="botnet.family home"><img className="brand-mark" src={`${import.meta.env.BASE_URL}favicon.svg`} alt=""/>botnet.family<span className="cursor"/></NavLink><nav className="nav" aria-label="Main">{NAV.map((item,i)=><span key={item.to} className="row" style={{"--gap":"10px"} as React.CSSProperties}>{i>0&&<span className="sep">/</span>}<NavLink to={item.to} end={item.end}>{item.label}</NavLink></span>)}{publicApiOrigin&&<><span className="sep">/</span><a href={`${publicApiOrigin}/v2/deployment`} target="_blank" rel="noreferrer">V2 API ↗</a></>}</nav><div className="header-right">{DEMO?<span className="tag outline">read-only preview</span>:<ConnectButton/>}</div></div>{DEMO?<DemoStrip/>:<StatusStrip/>}</header><main><div className="container"><Outlet/></div></main><footer className="footer"><div className="container spread"><span>botnet.family · public Registry V2 records</span><span className="small muted">Public lifecycle data is not a hosting-health check.</span></div></footer></div></>}
